import React from "react";
import { Row, Col, Form } from "react-bootstrap";
import { UseFormReturn, Controller } from "react-hook-form";
import { DisplayFormType, LocaleCode } from "../types";
import { renderImageComponent, ImageManagerProvider } from "../../shared/widgets/ImageManager";
import { usePropertyLocales } from "../../../components/property-locale-provider";
import { TranslationComponent } from "../../shared/widgets/TranslationComponent";
import type { DisplayImage } from "../types";
import { useConfig } from "../../../lib/config";
import { useIntl, FormattedMessage } from "react-intl";

import { ErrorFieldMessage } from "../components/ErrorFieldMessage";
import { messages } from "../messages";
import { DisplayContentWrapper } from "../styles";

interface ContentPanelProps {
  methods: UseFormReturn<DisplayFormType>;
  setImageFiles: (files: DisplayImage[]) => void;
  imageFiles: DisplayImage[];
}

export const ContentPanel: React.FC<ContentPanelProps> = ({
  methods,
  setImageFiles,
  imageFiles,
}) => {
  const { register, control, formState, watch } = methods;
  const { errors } = formState;
  const allowedLocales = usePropertyLocales();
  const config = useConfig();
  const contentType = watch("contentType");
  const intl = useIntl();
  const [hasImageError, setHasImageError] = React.useState(false);

  const ImageManagerAdapter = ({ field, locales }: { field: any; locales: LocaleCode[] }) => {
    const helpText = intl.formatMessage(messages.imagesHelpText);

    const handleImageChange = (value: DisplayImage[], payload?: any) => {
      let errorDetected = false;
      if (payload && payload.errors) {
        const { overSizeLimit, containsSpecial, belowMinWidth } = payload.errors;
        if (
          (overSizeLimit && overSizeLimit.length > 0) ||
          (containsSpecial && containsSpecial.length > 0) ||
          (belowMinWidth && belowMinWidth.length > 0)
        ) {
          errorDetected = true;
        }
      }
      if (config.mediaMaxFiles && value.length > config.mediaMaxFiles) {
        errorDetected = true;
      }
      value.forEach((img: any) => {
        const allowedFormats = ["image/jpeg", "image/jpg", "image/png"];
        if (img.type && !allowedFormats.includes(img.type)) {
          errorDetected = true;
        }
      });
      setHasImageError(errorDetected);
      field.onChange(value);
      setImageFiles(value);
    };

    return (
      <div className="image-manager-widget">
        <div style={{ color: "#FFA500", marginTop: 8 }}>
          {helpText.split("\n").map((line, idx) => (
            <div key={idx}>{line}</div>
          ))}
        </div>
        {renderImageComponent({
          schema: { title: messages.image.defaultMessage },
          idSchema: { $id: "imageManager" },
          formData: imageFiles.length > 0 ? imageFiles : field.value ?? [],
          onChange: handleImageChange,
          isDisabled: false,
          config: {
            locales,
            maxSizeMB: config.mediaMaxSizeMB,
            maxFiles: config.mediaMaxFiles,
            minWidth: config.mediaMinWidth,
            formats: "jpg,png,jpeg",
          },
        })}
        {hasImageError && (
          <div style={{ marginTop: 12 }}>
            <FormattedMessage {...messages.saveError} />
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={{ padding: "0 26px 26px 26px" }}>
      <DisplayContentWrapper>
        {/* HTML */}
        {contentType === "HTML" && (
          <div className="translation-component">
            <TranslationComponent allowedLocales={allowedLocales} />
          </div>
        )}

        {/* VIDEO */}
        {contentType === "VIDEO" && (
          <>
            <Row className="mb-2">
              <Col md={4}>
                <Form.Group>
                  <Form.Label>URL de Youtube</Form.Label>
                  <Form.Control {...register("youTubeURL")} placeholder="https://youtu.be/..." />
                  {errors?.youTubeURL && (
                    <Form.Text className="text-danger">{errors.youTubeURL.message}</Form.Text>
                  )}
                </Form.Group>
              </Col>
            </Row>
            <div className="translation-component">
              <TranslationComponent allowedLocales={allowedLocales} />
            </div>
          </>
        )}

        {/* IMAGE */}
        {contentType === "IMAGE" && (
          <ImageManagerProvider>
            <Row className="mb-4">
              <Col>
                <Form.Group>
                  <Controller
                    name="imageManager"
                    control={control}
                    render={({ field }) => (
                      <ImageManagerAdapter field={field} locales={allowedLocales as LocaleCode[]} />
                    )}
                  />
                  {Object.values(errors).length > 0 && (
                    <ErrorFieldMessage message={messages.propertiesTabValidator.defaultMessage} />
                  )}
                </Form.Group>
              </Col>
            </Row>
            <div className="translation-component">
              <TranslationComponent allowedLocales={allowedLocales} />
            </div>
          </ImageManagerProvider>
        )}
      </DisplayContentWrapper>
    </div>
  );
};
